import React, { createContext, useContext, useEffect, useState } from "react";

import Keycloak from 'keycloak-js';
import axios from "axios";
import Loader from "components/Loader";

const { REACT_APP_KEYCLOAK_URL, REACT_APP_KEYCLOAK_REALM, REACT_APP_KEYCLOAK_CLIENT_ID, REACT_APP_SERVER_URL } = process.env;

const realm = REACT_APP_KEYCLOAK_REALM;
const client = REACT_APP_KEYCLOAK_CLIENT_ID;
const IS_DEVELOPMENT = process.env.NODE_ENV !== 'production';

export const keycloak = new Keycloak({
  url: REACT_APP_KEYCLOAK_URL,
  realm: realm,
  clientId: client,
});

function clearStaleKeycloakTokens() {
  if (localStorage.getItem('keycloaktokens-reset')) {
    Object.keys(localStorage)
      .filter(key => key.startsWith('kc-'))
      .forEach(key => localStorage.removeItem(key));
    localStorage.setItem('keycloaktokens-reset', true);
  }
}

// Invite links (/invite/<token>) are opened by people who have no account yet,
// so this is handled here, before the login-required init below would send
// them to the sign-in page. Redirect-only: resolve the token to an email, then
// hand off to Keycloak's own registration page with it as login_hint.
const INVITE_PATH = /^\/invite\/([^/]+)\/?$/;

// A link invite has no email to match on, so its token is carried through
// signup in this browser and sent with the first /users/me (ProfileProvider).
const INVITE_TOKEN_KEY = "nctc-invite-token";

export const readInviteToken = () => {
  try {
    return localStorage.getItem(INVITE_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const clearInviteToken = () => {
  try {
    localStorage.removeItem(INVITE_TOKEN_KEY);
  } catch {
    // Storage unavailable: nothing was stored.
  }
};

const redirectToInviteRegistration = async (token) => {
  try {
    localStorage.setItem(INVITE_TOKEN_KEY, token);
  } catch {
    // Storage unavailable: the invite can't be claimed, registration still works.
  }
  let loginHint;
  try {
    // Plain axios on purpose: axiosInstance attaches a Keycloak token and logs
    // out on failure, and this visitor is not signed in.
    const { data } = await axios.get(`${REACT_APP_SERVER_URL}/invites/resolve/${encodeURIComponent(token)}`);
    loginHint = data.email;
  } catch (err) {
    // Unknown/expired/cancelled invite: registration is open, just no pre-fill.
  }
  await keycloak.register({ loginHint, redirectUri: `${window.location.origin}/` });
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (keycloak.didInitialize) { 
      setInitialized(true);
      setAuthenticated(keycloak.authenticated); 
      return;
    }

    // Temporary fix: clear stale Keycloak tokens from localStorage
    clearStaleKeycloakTokens();

    const inviteMatch = window.location.pathname.match(INVITE_PATH);
    if (inviteMatch) {
      keycloak.init({
        // check-sso: notice an existing session instead of registering over it.
        onLoad: "check-sso",
        pkceMethod: "S256",
        flow: "standard",
        checkLoginIframe: false,
        responseMode: "fragment",
      })
        .then((authenticated) => {
          // Already signed in: nothing to register, go to the app.
          if (authenticated) return window.location.replace("/");
          return redirectToInviteRegistration(inviteMatch[1]);
        })
        .catch((err) => {
          console.error("Keycloak initialization failed:", err);
          setError(err);
        });
      return;
    }

    keycloak.init({
      onLoad: "login-required",
      pkceMethod: "S256",
      flow: "standard",
      checkLoginIframe: false,
      checkLoginIframeInterval: 0,
      enableLogging: IS_DEVELOPMENT,
      responseMode: "fragment",
      redirectUri: `${window.location.origin}${window.location.pathname}${window.location.search}`,
    })
    .then((authenticated) => {
      if (authenticated) {
        keycloak.onTokenExpired = () => {
          keycloak.updateToken(30).catch(() => {
            keycloak.login();
          });
        };
        
        setAuthenticated(true);
        setInitialized(true);
      }
    })
    .catch((err) => {
      console.error("Keycloak initialization failed:", err);
      setError(err);
    });
  }, []);

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Authentication Error</h2>
        <p>Unable to connect. Please refresh the page.</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  // Wait for authentication
  if (!initialized || !authenticated) {
    return <Loader />;
  }

  return (
    <AuthContext.Provider value={keycloak}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
